import { useState, useEffect, useRef } from 'react'
import { TopNav, Footer } from '../components/PublicLayout'
import SEO from '../components/SEO'
import { useToast } from '../components/Toast'
import { supabase } from '../lib/supabase'

// ─── Contact page ────────────────────────────────────────────────────────────
//
// Public marketing page for prospects to get in touch. Submits via the
// existing send-email Edge Function (type: 'contact_form'), which forwards
// the message to the practice's inbox via Resend.
//
// Spam protection: three lightweight layers, no CAPTCHA.
//   1. Honeypot field (`website`) hidden from humans, filled by bots → reject
//   2. Time-trap: form must be on the page for ≥2 seconds before submit
//   3. Length caps on every field so the endpoint can't be abused for relay
//
// If spam volume becomes a problem, swap in Cloudflare Turnstile later.

const CONTACT_EMAIL = 'hello@receiptchaser.com'

// ─── Page ────────────────────────────────────────────────────────────────────

export default function Contact() {
  const toast = useToast()

  const [name, setName]       = useState('')
  const [email, setEmail]     = useState('')
  const [firm, setFirm]       = useState('')
  const [message, setMessage] = useState('')
  const [website, setWebsite] = useState('') // honeypot, leave hidden

  const [submitting, setSubmitting] = useState(false)
  const [sent, setSent]             = useState(false)
  const [error, setError]           = useState('')

  // Time-trap: record when the page mounted; reject submissions <2s later.
  const mountedAt = useRef(Date.now())

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // ─── Validation ────────────────────────────────────────────────────────────
  // Keep validation light: single regex for email, length caps elsewhere.
  // Real validation happens server-side too.
  function validate() {
    if (!name.trim())    return 'Please enter your name.'
    if (!email.trim())   return 'Please enter your email address.'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      return "That email address doesn't look quite right."
    }
    if (!message.trim()) return 'Please add a message.'
    if (message.trim().length < 10) {
      return 'Your message is a bit short. Please give us a few more details.'
    }
    return null
  }

  // ─── Submit ────────────────────────────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault()
    setError('')

    // Honeypot: silently pretend success so bots don't learn to bypass.
    if (website) {
      setSent(true)
      return
    }

    // Time-trap: humans don't fill and submit in under 2 seconds.
    if (Date.now() - mountedAt.current < 2000) {
      setSent(true)
      return
    }

    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }

    setSubmitting(true)
    try {
      const { error: invokeError } = await supabase.functions.invoke('send-email', {
        body: {
          type: 'contact_form',
          name:    name.trim().slice(0, 100),
          email:   email.trim().slice(0, 200),
          firm:    firm.trim().slice(0, 150),
          message: message.trim().slice(0, 5000),
        },
      })
      if (invokeError) throw invokeError

      setSent(true)
      toast('Message sent. We will be in touch soon.', 'success')
    } catch (err) {
      console.error('Contact form error:', err)
      setError("Sorry, something went wrong sending your message. Please email us directly at " + CONTACT_EMAIL + ".")
    } finally {
      setSubmitting(false)
    }
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ background: 'var(--surface)', minHeight: '100vh' }}>
      <SEO
        title="Contact Receipt Chaser"
        description="Questions about Receipt Chaser? We read every message. Send us a note about how the product fits your bookkeeping practice and we'll get back to you shortly."
        path="/contact"
      />
      <TopNav />

      {/* Intro */}
      <section style={{ padding: '64px 0 24px' }}>
        <div className="container" style={{
          padding: '0 24px', maxWidth: 720, textAlign: 'center',
        }}>
          <p style={{
            fontSize: '0.78rem', fontWeight: 600,
            color: 'var(--accent)', textTransform: 'uppercase',
            letterSpacing: '0.05em', marginBottom: 12,
          }}>
            Get in touch
          </p>
          <h1 style={{
            marginBottom: 18, lineHeight: 1.15,
            fontSize: 'clamp(2rem, 5vw, 2.6rem)',
          }}>
            We're a small team,<br/>and we read every message.
          </h1>
          <p style={{ color: 'var(--ink-muted)', fontSize: '1.05rem', lineHeight: 1.6 }}>
            Wondering if Receipt Chaser is right for your practice? Want to know
            how it compares to what you're using today? Send us a note and we'll
            give you a straight answer.
          </p>
        </div>
      </section>

      {/* Form + side panel */}
      <section style={{ padding: '24px 0 80px' }}>
        <div className="container" style={{ padding: '0 24px', maxWidth: 980 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 40,
            alignItems: 'start',
          }}>
            {/* Left: form card */}
            <div style={{
              background: 'var(--white)',
              borderRadius: 'var(--radius-lg)',
              padding: 32,
              boxShadow: 'var(--shadow-sm)',
              border: '1px solid var(--surface-2)',
            }}>
              {sent ? (
                <SuccessState />
              ) : (
                <form onSubmit={handleSubmit} noValidate>
                  <FormField label="Your name" htmlFor="contact-name">
                    <input
                      id="contact-name"
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      maxLength={100}
                      autoComplete="name"
                      placeholder="Jane Smith"
                      style={inputStyle}
                      disabled={submitting}
                      required
                    />
                  </FormField>

                  <FormField label="Email address" htmlFor="contact-email">
                    <input
                      id="contact-email"
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      maxLength={200}
                      autoComplete="email"
                      placeholder="jane@yourpractice.co.uk"
                      style={inputStyle}
                      disabled={submitting}
                      required
                    />
                  </FormField>

                  <FormField
                    label="Practice or firm"
                    htmlFor="contact-firm"
                    optional
                  >
                    <input
                      id="contact-firm"
                      type="text"
                      value={firm}
                      onChange={e => setFirm(e.target.value)}
                      maxLength={150}
                      autoComplete="organization"
                      placeholder="Acme Bookkeeping Ltd"
                      style={inputStyle}
                      disabled={submitting}
                    />
                  </FormField>

                  <FormField label="Your message" htmlFor="contact-message">
                    <textarea
                      id="contact-message"
                      value={message}
                      onChange={e => setMessage(e.target.value)}
                      maxLength={5000}
                      rows={6}
                      placeholder="What can we help with?"
                      style={{ ...inputStyle, resize: 'vertical', minHeight: 120, fontFamily: 'inherit' }}
                      disabled={submitting}
                      required
                    />
                  </FormField>

                  {/* Honeypot: visually hidden, kept in DOM for bots. */}
                  <div
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      left: '-9999px',
                      width: 1, height: 1, overflow: 'hidden',
                    }}
                  >
                    <label htmlFor="contact-website">Website (leave blank)</label>
                    <input
                      id="contact-website"
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      value={website}
                      onChange={e => setWebsite(e.target.value)}
                    />
                  </div>

                  {error && (
                    <div style={{
                      background: '#fef2f2',
                      border: '1px solid #fecaca',
                      color: '#991b1b',
                      padding: '10px 14px',
                      borderRadius: 8,
                      fontSize: '0.88rem',
                      marginBottom: 16,
                      lineHeight: 1.5,
                    }}>
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={submitting}
                    style={{
                      padding: '12px 28px',
                      fontSize: '0.95rem',
                      width: '100%',
                      opacity: submitting ? 0.7 : 1,
                      cursor: submitting ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {submitting ? 'Sending…' : 'Send message →'}
                  </button>

                  <p style={{
                    fontSize: '0.78rem',
                    color: 'var(--ink-faint)',
                    marginTop: 14,
                    lineHeight: 1.5,
                    textAlign: 'center',
                  }}>
                    We'll only use your details to reply to your enquiry.
                    No marketing, no list, no nonsense.
                  </p>
                </form>
              )}
            </div>

            {/* Right: side panel with alternative contact and prospect-focused prompts */}
            <aside style={{ padding: '8px 0' }}>
              <SidePanelBlock
                heading="Prefer email?"
                body={
                  <>
                    Drop us a line at{' '}
                    <a
                      href={`mailto:${CONTACT_EMAIL}`}
                      style={{ color: 'var(--accent)', fontWeight: 500 }}
                    >
                      {CONTACT_EMAIL}
                    </a>
                    {' '}and it goes to the same place as the form.
                  </>
                }
              />

              <SidePanelBlock
                heading="Thinking about Receipt Chaser?"
                body="Tell us a bit about your practice. Roughly how many clients, which accounting software you use, and what's not working with how you collect receipts today. We'll give you a straight answer about whether it's a fit."
              />

              <SidePanelBlock
                heading="Have a question we haven't answered?"
                body="If something on the website isn't clear, or you can't find an answer to a specific question about how the product works, just ask."
              />
            </aside>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

// ─── Bits ────────────────────────────────────────────────────────────────────

function FormField({ label, htmlFor, optional, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label
        htmlFor={htmlFor}
        style={{
          display: 'block',
          fontSize: '0.85rem',
          fontWeight: 500,
          color: 'var(--ink)',
          marginBottom: 6,
        }}
      >
        {label}
        {optional && (
          <span style={{ color: 'var(--ink-faint)', fontWeight: 400, marginLeft: 6 }}>
            (optional)
          </span>
        )}
      </label>
      {children}
    </div>
  )
}

function SidePanelBlock({ heading, body }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{
        fontSize: '0.95rem',
        fontWeight: 600,
        color: 'var(--ink)',
        marginBottom: 6,
        lineHeight: 1.3,
      }}>
        {heading}
      </h3>
      <p style={{
        fontSize: '0.9rem',
        color: 'var(--ink-muted)',
        lineHeight: 1.6,
        margin: 0,
      }}>
        {body}
      </p>
    </div>
  )
}

function SuccessState() {
  return (
    <div style={{ textAlign: 'center', padding: '24px 8px' }}>
      <div style={{
        width: 52, height: 52,
        borderRadius: '50%',
        background: 'var(--accent-light)',
        color: 'var(--accent)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.6rem',
        marginBottom: 18,
      }}>
        ✓
      </div>
      <h2 style={{
        fontSize: '1.4rem',
        marginBottom: 10,
        lineHeight: 1.3,
      }}>
        Got it, thanks.
      </h2>
      <p style={{
        color: 'var(--ink-muted)',
        fontSize: '0.95rem',
        lineHeight: 1.6,
        maxWidth: 380,
        margin: '0 auto',
      }}>
        We'll get back to you shortly. Keep an eye on the inbox you used to write to us.
      </p>
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  fontSize: '0.95rem',
  border: '1px solid var(--surface-2)',
  borderRadius: 8,
  background: 'var(--white)',
  color: 'var(--ink)',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.15s, box-shadow 0.15s',
  boxSizing: 'border-box',
}
